/**
 * سربرگِ «سایت‌های رقیب» — تنها جایی که فهرستِ استثنای نشانِ لینک‌های
 * خروجی نگهداری می‌شود (مایگریشن 0134، WHOSUP-167).
 *
 * ═══ این فهرست چه می‌کند ═══
 *
 * هر لینکی که کاربر را از اپ یا سایت به بیرون می‌برد (صفحه‌ی رسمیِ رخداد،
 * خریدِ بلیط) خودکار `utm_source=outami.com` می‌گیرد تا برگزارکننده در
 * آمارش ببیند ترافیک از ما آمده — **مگر** مقصد یکی از همین دامنه‌ها باشد.
 * به رقیب دلیلی ندارد بگوییم کاربرش را ما فرستادیم.
 *
 * قاعده‌ی تطبیق در `core/lib/outboundUrl.ts` است: زیردامنه هم می‌گیرد
 * («www.eventbrite.com» و «x.eventbrite.com» هر دو با «eventbrite.com»).
 * ولی «eventbrite.ca» دامنه‌ی جداست و ردیفِ خودش را می‌خواهد.
 *
 * تغییرِ فهرست همان لحظه در دیتابیس می‌نشیند؛ اپ‌ها آن را موقعِ **بوت**
 * می‌خوانند، پس روی گوشی/مرورگرِ باز از اجرای بعدی اثر می‌کند.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { competitorRepo } from '@/core/data/competitorRepo';
import { reportError } from '@/core/lib/report';

export function CompetitorsTab() {
  const qc = useQueryClient();
  const [domain, setDomain] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const rows = useQuery({ queryKey: ['competitorDomains'], queryFn: competitorRepo.list });

  const fail = (where: string) => (e: unknown) => {
    setErr(e instanceof Error ? e.message : String(e));
    reportError(where, e);
  };

  const add = useMutation({
    mutationFn: () => competitorRepo.add(domain, note),
    onSuccess: () => {
      setErr(null);
      setDone(`«${domain.trim()}» اضافه شد — از بوتِ بعدیِ اپ‌ها لینکی به‌سویش نشان نمی‌گیرد`);
      setDomain('');
      setNote('');
      qc.invalidateQueries({ queryKey: ['competitorDomains'] });
    },
    onError: fail('admin.addCompetitorDomain'),
  });

  const remove = useMutation({
    mutationFn: (d: string) => competitorRepo.remove(d),
    onSuccess: (_r, d) => {
      setErr(null);
      setDone(`«${d}» برداشته شد — لینک‌های به‌سویش دوباره نشان می‌گیرند`);
      qc.invalidateQueries({ queryKey: ['competitorDomains'] });
    },
    onError: fail('admin.removeCompetitorDomain'),
  });

  if (rows.isLoading) return <p className="dim">…</p>;
  if (rows.error) return <p className="err">فهرستِ سایت‌های رقیب نیامد.</p>;

  return (
    <div className="card">
      <h2>سایت‌های رقیب</h2>
      <p className="dim" style={{ marginBottom: 10 }}>
        هر لینکِ خروجی (صفحه‌ی رسمیِ رخداد، خریدِ بلیط) خودکار «utm_source=outami.com»
        می‌گیرد تا مقصد ببیند ترافیک از ما آمده — مگر مقصد یکی از این دامنه‌ها باشد.
      </p>
      <p className="dim" style={{ marginBottom: 10 }}>
        زیردامنه‌ها خودکار پوشیده می‌شوند (www و بقیه)، ولی پسوندِ متفاوت دامنه‌ی
        جداست: «eventbrite.ca» ردیفِ خودش را می‌خواهد. لینکِ کامل هم بچسبانی اشکالی
        ندارد — فقط دامنه‌اش نگه داشته می‌شود.
      </p>

      <div className="filters" style={{ marginBottom: 10 }}>
        <input placeholder="دامنه — مثلاً eventbrite.com" value={domain} dir="ltr"
          onChange={(e) => setDomain(e.target.value)} />
        <input placeholder="یادداشت (اختیاری)" value={note}
          onChange={(e) => setNote(e.target.value)} />
        <button type="button" className="btn"
          disabled={add.isPending || !domain.trim()}
          onClick={() => { setDone(null); add.mutate(); }}>
          افزودن
        </button>
        <span className="spacer" />
        <span className="dim">{(rows.data ?? []).length} دامنه</span>
      </div>

      {err ? <p className="err" style={{ marginBottom: 8 }}>{err}</p> : null}
      {done ? <p className="dim" style={{ marginBottom: 8 }}>{done}</p> : null}

      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr>
              <th>دامنه</th>
              <th>یادداشت</th>
              <th>افزوده‌شده</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(rows.data ?? []).map((r) => (
              <tr key={r.domain}>
                <td className="mono" dir="ltr">{r.domain}</td>
                <td className="dim">{r.note || '—'}</td>
                <td className="dim mono" dir="ltr">{r.addedAt ? r.addedAt.slice(0, 10) : '—'}</td>
                <td>
                  {/* تاییدِ دوم ندارد — حذف برگشت‌پذیر است، همان دکمه‌ی افزودن
                      برش می‌گرداند (همان قاعده‌ی «کنار بگذار» در تگ‌ها). */}
                  <button type="button" className="btn ghost"
                    title="لینک‌های به‌سوی این دامنه دوباره نشان می‌گیرند"
                    disabled={remove.isPending}
                    onClick={() => { setDone(null); remove.mutate(r.domain); }}>
                    حذف
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
